import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupervision } from '../hooks/useSupervision';
import { useApi } from '../hooks/useApi';
import { getTheme } from '../themes';
import { computeLayout, ROOM_KEYS } from '../components/studio/layout';
import StudioScene from '../components/studio/StudioScene';
import Patchbay from '../components/studio/Patchbay';
import RoomSheet from '../components/studio/RoomSheet';
import Talkback from '../components/supervision/Talkback';
import StationSheet from '../components/studio/StationSheet';
import '../components/studio/studio.css';

const MAX_FLIGHTS = 12;
const FEED_SIZE = 30;

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Estudio (Fase 9.2), solo admin: la operación de IA y de asesores como una
 * escena en vivo. Es una vista: ninguna decisión del portal depende de ella.
 */
export default function StudioPage({ onBack, onOpenTable }) {
  const { apiFetch } = useApi();
  const [view, setView] = useState('overview');
  const [flights, setFlights] = useState([]);
  const [feed, setFeed] = useState([]);
  // Puesto seleccionado: { kind: 'ai' | 'human', id, room }
  const [selected, setSelected] = useState(null);
  const selectedRef = useRef(null);
  selectedRef.current = selected;

  // Animaciones y hoja de actividad a partir de los mensajes en vivo
  const onLive = useCallback((msg, prev) => {
    if (msg.type === 'portal_event' && msg.data?.actor_kind === 'ai_agent') {
      setFeed(f => [{ ...msg.data, coalesced: msg.coalesced || 1 }, ...f].slice(0, FEED_SIZE));
      return;
    }
    if (prefersReducedMotion()) return;
    const d = msg.data || {};
    const rowOf = sid => prev?.sessions.rows.find(r => r.session_id === sid);
    let from = null;
    let to = null;
    // Las escalaciones salen de la cabina de Escalación (si el catálogo la tiene) o de control
    const escalator = prev?.ai_agents.some(a => a.id === 'escalation') ? 'agent:escalation' : 'control';
    if (msg.type === 'conversation_queued') { from = escalator; to = 'lobby'; }
    if (msg.type === 'conversation_requeued' && d.from_user) { from = `human:${d.from_user}`; to = 'lobby'; }
    if (msg.type === 'conversation_assigned' && d.to_user) {
      to = `human:${d.to_user.id}`;
      from = d.from_user ? `human:${d.from_user}` : rowOf(d.session_id)?.state === 'queued' ? 'lobby' : escalator;
    }
    if (msg.type === 'conversation_closed') {
      const row = rowOf(d.session_id);
      if (row?.assigned_user_id) { from = `human:${row.assigned_user_id}`; to = 'control'; }
    }
    if (from && to && from !== to) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setFlights(fl => (fl.length >= MAX_FLIGHTS ? fl : [...fl, { id, from, to }]));
    }
  }, []);

  const { data, error, live, now, wsStatus } = useSupervision(onLive);
  const theme = getTheme(data?.studio?.theme);
  const layout = useMemo(() => (data ? computeLayout(data) : null), [data]);

  // Actividad reciente de la IA al abrir (después llega en vivo)
  useEffect(() => {
    apiFetch('/api/admin/events?kind=ai_agent&limit=20')
      .then(rows => setFeed(f => (f.length ? f : rows)))
      .catch(() => {});
  }, [apiFetch]);

  // Esc vuelve a la vista general
  useEffect(() => {
    const onKey = e => {
      if (e.key !== 'Escape') return;
      // Esc: primero cierra las tareas del puesto; luego vuelve a la vista general
      if (selectedRef.current) setSelected(null);
      else setView('overview');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const leadName = data?.ai_agents.find(a => a.room === 'control')?.name || 'la IA';
  const selectStation = useCallback(st => { setView(st.room); setSelected(st); }, []);
  const doneFlight = useCallback(id => setFlights(fl => fl.filter(f => f.id !== id)), []);
  const c = theme.colors;

  return (
    // El estudio es una escena oscura: sus paneles usan los tokens del modo oscuro
    <div data-theme="dark" style={{ ...s.root, background: c.bg }}>
      <header style={s.header}>
        <button onClick={onBack} style={s.backBtn}>← Volver al chat</button>
        <h1 style={s.title}>{data?.studio?.title || 'Estudio'}</h1>
        <span style={s.live} title="Conexión en vivo con el portal">
          <span style={{ color: wsStatus === 'connected' ? 'var(--success)' : 'var(--warning)' }}>●</span>
          {wsStatus === 'connected' ? ' En vivo' : ' Reconectando…'}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={onOpenTable} style={s.linkBtn}>Ver en tabla</button>
      </header>

      <nav style={s.nav} aria-label="Salas">
        {['overview', ...ROOM_KEYS].map(k => (
          <button key={k} onClick={() => { setView(k); setSelected(null); }}
            style={{ ...s.chip, ...(view === k ? { ...s.chipActive, borderColor: c.accent } : {}) }}
            aria-pressed={view === k}>
            {k === 'overview' ? 'Vista general' : theme.rooms[k].label}
          </button>
        ))}
        {view !== 'overview' && <span style={s.hint}>Esc para volver</span>}
      </nav>

      {error && <div style={s.errorBox}>⚠️ {error}</div>}
      {!data && !error && <div style={s.hint}>Preparando el estudio…</div>}

      {data && layout && (
        <main style={s.main}>
          <div style={s.stage}>
            <StudioScene data={data} layout={layout} theme={theme} live={live} now={now}
              view={view} onOpenRoom={k => { setView(k); setSelected(null); }} flights={flights} onFlightDone={doneFlight}
              selected={selected} onSelectStation={selectStation} />
            <div style={s.patchbay}>
              <div style={s.patchTitle}>{theme.words.patchbay} <span style={s.hint}>· {theme.words.patchbayHint}</span></div>
              <Patchbay integrations={data.integrations} theme={theme} />
            </div>
          </div>
          <div style={s.side}>
            {selected
              ? <StationSheet station={selected} data={data} theme={theme} live={live} now={now} feed={feed} onClose={() => setSelected(null)} />
              : <RoomSheet view={view} data={data} theme={theme} live={live} now={now} feed={feed} onSelectStation={selectStation} />}
            {/* Talkback siempre a la mano, debajo de la hoja (conserva la conversación al cambiar de sala) */}
            <div style={{ ...s.talk, borderColor: c.cardBorder }}>
              <Talkback name={leadName} compact />
            </div>
          </div>
        </main>
      )}
    </div>
  );
}

const s = {
  root: { minHeight: '100vh', color: 'var(--text)', padding: '16px 20px 28px', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  header: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 12 },
  backBtn: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '6px 12px', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer' },
  linkBtn: { background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '6px 12px', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer' },
  title: { fontSize: 20, fontWeight: 800, color: 'var(--text)', margin: 0 },
  live: { fontSize: 12, color: 'var(--text-2)' },
  nav: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 },
  chip: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 20, padding: '6px 14px', fontSize: 12.5, color: 'var(--text-2)', cursor: 'pointer' },
  chipActive: { background: 'var(--surface-3)', color: 'var(--text)' },
  hint: { fontSize: 12, color: 'var(--text-3)' },
  main: { display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' },
  stage: { flex: '1 1 640px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  side: { flex: '0 1 360px', minWidth: 280, width: '100%', display: 'flex', flexDirection: 'column', gap: 12 },
  patchbay: { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 14, padding: '10px 12px 0' },
  patchTitle: { fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.8 },
  talk: { background: 'var(--surface)', border: '1px solid', borderRadius: 14, padding: 14 },
  errorBox: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 10, padding: '10px 14px', color: 'var(--danger)', fontSize: 13, marginBottom: 12 },
};
